import { motion } from "framer-motion";
import { Clock, Zap, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

export const isOpenNow = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Madrid",
    weekday: "short",
    hour: "numeric",
    hour12: false,
  }).formatToParts(date);
  const day = parts.find((p) => p.type === "weekday")?.value;
  const hour = Number(parts.find((p) => p.type === "hour")?.value);
  return day !== "Sun" && hour >= 8 && hour < 20;
};

export const AvailabilityBar = () => {
  const { t } = useLanguage();
  const open = isOpenNow();
  return (
    <div
      data-testid="availability-bar"
      className="border-t border-[#0D1B2A]/10 bg-[#0D1B2A] text-[#FAF6F0]"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-1.5 text-[11px] font-semibold sm:px-6 sm:text-xs lg:px-8">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <span data-testid="availability-status" className="inline-flex shrink-0 items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              {open && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75" />}
              <span className={`relative inline-flex h-2 w-2 rounded-full ${open ? "bg-[#25D366]" : "bg-[#F2A33C]"}`} />
            </span>
            {open ? t.hours.openNow : t.hours.closedNow}
          </span>
          <span className="hidden items-center gap-1.5 text-[#FAF6F0]/60 sm:inline-flex" data-testid="availability-schedule">
            <Clock size={12} />
            {t.hours.schedule} · {t.hours.sundays}
          </span>
        </div>
        <a
          data-testid="availability-urgent-link"
          href={waLink(t.hours.waUrgent)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 text-[#F2A33C] transition-colors duration-300 hover:text-white"
        >
          <Zap size={12} strokeWidth={2.6} className="fill-current" />
          {t.hours.sameDay}
          <span className="hidden text-[#FAF6F0]/60 sm:inline">· {t.hours.barCta}</span>
        </a>
      </div>
    </div>
  );
};

export const EmergencyBlock = () => {
  const { t } = useLanguage();
  return (
    <motion.div
      data-testid="emergency-block"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative mt-20 overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-7 backdrop-blur-md sm:p-10"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#F2A33C]/15 blur-3xl" />
      <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#F2A33C]">
            <Zap size={14} className="fill-current" />
            {t.hours.sameDay}
          </p>
          <h3 className="font-serif text-3xl font-black tracking-tight sm:text-4xl">{t.hours.title}</h3>
          <p className="mt-3 max-w-lg text-base leading-relaxed text-[#FAF6F0]/60">{t.hours.sub}</p>
          <a
            data-testid="emergency-whatsapp-cta"
            href={waLink(t.hours.waUrgent)}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#F2A33C] px-7 py-3.5 text-base font-bold text-[#0D1B2A] shadow-xl shadow-[#F2A33C]/25 transition-all duration-300 hover:scale-[1.04] hover:bg-[#F7B85A]"
          >
            <MessageCircle size={18} strokeWidth={2.4} className="transition-transform duration-300 group-hover:-rotate-12" />
            {t.hours.cta}
          </a>
        </div>
        <dl className="grid gap-3 lg:col-span-5">
          <div className="flex items-start gap-4 rounded-2xl bg-[#FAF6F0] p-5 text-[#0D1B2A]">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1B6B93] text-white">
              <Clock size={20} />
            </span>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D1B2A]/50">{t.hours.scheduleLabel}</dt>
              <dd data-testid="emergency-schedule" className="mt-1 font-bold leading-snug">
                {t.hours.schedule}
                <span className="block text-sm font-medium text-[#0D1B2A]/60">{t.hours.sundays}</span>
              </dd>
            </div>
          </div>
          <div className="flex items-start gap-4 rounded-2xl bg-[#FAF6F0] p-5 text-[#0D1B2A]">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C85A32] text-white">
              <Zap size={20} className="fill-current" />
            </span>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D1B2A]/50">{t.hours.urgentLabel}</dt>
              <dd data-testid="emergency-urgent" className="mt-1 font-bold leading-snug">{t.hours.urgentValue}</dd>
            </div>
          </div>
        </dl>
      </div>
    </motion.div>
  );
};
